/**
 * ExerciseField Use Case Hook
 * Manages exercise selection and configuration
 * Requirements: 4.2
 */

import {useState, useCallback, useMemo} from 'react';
import {UseFormReturn} from 'react-hook-form';
import type {WorkoutRoutineFormData} from '../../WorkoutRoutineForm.schema';
import type {
  UseExerciseFieldReturn,
  ExerciseSelectOption,
  ExerciseDisplayData,
  SelectedExerciseData,
} from './ExerciseField.type';

/**
 * Hook for managing exercise field state and actions
 *
 * @param form - React Hook Form instance
 * @param groupIndex - Index of the parent workout group
 * @param workoutIndex - Index of the parent workout
 * @param exerciseIndex - Index of this exercise
 * @returns Exercise field state and action handlers
 */
export function useExerciseField(
  form: UseFormReturn<WorkoutRoutineFormData>,
  groupIndex: number,
  workoutIndex: number,
  exerciseIndex: number,
): UseExerciseFieldReturn {
  const [isEditing, setIsEditing] = useState(false);

  // Get current exercise data from form
  const exerciseWorkout = form.watch(
    `workoutGroups.${groupIndex}.workouts.${workoutIndex}.exerciseWorkouts.${exerciseIndex}`,
  );

  // Selected exercise state
  const selectedExercise = useMemo<ExerciseSelectOption | null>(() => {
    if (!exerciseWorkout?.exercise) return null;
    return {
      value: exerciseWorkout.exercise.value || exerciseWorkout.exerciseId,
      label: exerciseWorkout.exercise.label || '',
      animatedImage: exerciseWorkout.exercise.animatedImage,
      image: exerciseWorkout.exercise.image,
    };
  }, [exerciseWorkout]);

  // Format exercise data for display
  const exerciseData = useMemo<ExerciseDisplayData>(() => {
    if (!exerciseWorkout) {
      return {
        name: 'Exercício não selecionado',
        imageUrl: null,
        details: {
          series: '',
          reps: '',
          weight: '',
          time: '',
          restTime: '',
          notes: '',
        },
      };
    }

    const name =
      exerciseWorkout.exercise?.label ||
      exerciseWorkout.exercise?.name ||
      'Exercício';
    const imageUrl =
      exerciseWorkout.exercise?.animatedImage ||
      exerciseWorkout.exercise?.image ||
      null;

    return {
      name,
      imageUrl,
      details: {
        series: exerciseWorkout.rounds || exerciseWorkout.series || '',
        reps: exerciseWorkout.repetitions || exerciseWorkout.reps || '',
        weight: exerciseWorkout.load || exerciseWorkout.weight || '',
        time: exerciseWorkout.time || exerciseWorkout.timeText || '',
        restTime: String(exerciseWorkout.restTime || ''),
        notes: exerciseWorkout.notes || '',
      },
    };
  }, [exerciseWorkout]);

  /**
   * Set selected exercise
   */
  const setSelectedExercise = useCallback(
    (exercise: ExerciseSelectOption | null) => {
      if (exercise) {
        form.setValue(
          `workoutGroups.${groupIndex}.workouts.${workoutIndex}.exerciseWorkouts.${exerciseIndex}.exerciseId`,
          exercise.value,
          {shouldDirty: true},
        );
        form.setValue(
          `workoutGroups.${groupIndex}.workouts.${workoutIndex}.exerciseWorkouts.${exerciseIndex}.exercise`,
          {
            value: exercise.value,
            label: exercise.label,
            animatedImage: exercise.animatedImage,
            image: exercise.image,
          },
          {shouldDirty: true},
        );
      }
    },
    [form, groupIndex, workoutIndex, exerciseIndex],
  );

  /**
   * Handle updating exercise from modal
   */
  const handleUpdateExercise = useCallback(
    (data: SelectedExerciseData) => {
      const basePath =
        `workoutGroups.${groupIndex}.workouts.${workoutIndex}.exerciseWorkouts.${exerciseIndex}` as const;

      // Update exercise reference
      form.setValue(`${basePath}.exerciseId`, data.exercise.id, {
        shouldDirty: true,
      });
      form.setValue(
        `${basePath}.exercise`,
        {
          value: data.exercise.id,
          label: data.exercise.name,
          animatedImage: data.exercise.animatedImage,
          image: data.exercise.image,
        },
        {shouldDirty: true},
      );

      // Update exercise details
      if (data.series !== undefined) {
        form.setValue(`${basePath}.rounds`, data.series, {shouldDirty: true});
      }
      if (data.reps !== undefined) {
        form.setValue(`${basePath}.repetitions`, data.reps, {
          shouldDirty: true,
        });
      }
      if (data.weight !== undefined) {
        form.setValue(`${basePath}.load`, data.weight, {shouldDirty: true});
      }
      if (data.time !== undefined) {
        form.setValue(`${basePath}.time`, data.time, {shouldDirty: true});
      }
      if (data.restTime !== undefined) {
        form.setValue(`${basePath}.restTime`, data.restTime, {
          shouldDirty: true,
        });
      }
      if (data.notes !== undefined) {
        form.setValue(`${basePath}.notes`, data.notes, {shouldDirty: true});
      }

      setIsEditing(false);
    },
    [form, groupIndex, workoutIndex, exerciseIndex],
  );

  return {
    selectedExercise,
    setSelectedExercise,
    exerciseData,
    isEditing,
    setIsEditing,
    handleUpdateExercise,
  };
}

/**
 * Format detail text for display
 */
export function formatDetailText(
  label: string,
  value: string | undefined,
): string | null {
  if (!value || value === '') return null;
  return `${label}: ${value}`;
}

/**
 * Check if exercise has any details
 */
export function hasAnyDetails(
  details: ExerciseDisplayData['details'],
): boolean {
  return Object.values(details).some((value) => value && value !== '');
}

export default useExerciseField;
